import { Epic, Session } from '../types';

export interface VelocityMetrics {
    stalledCount: number;
    freshCount: number;
    agingCount: number;
    laneChanges: number;
    healthRatio: number;
}

/**
 * Calculate dates since
 */
export function daysSince(date: string | Date | undefined): number {
    if (!date) return Infinity;
    const now = new Date();
    const then = new Date(date);
    const diffMs = now.getTime() - then.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Check if the epic is "stalled" (> thresholdDays since last update)
 */
export function isEpicStalled(epic: Epic, thresholdDays = 30): boolean {
    const lastActivity = epic.updatedAt || epic.createdAt;
    const days = daysSince(lastActivity);
    return days > thresholdDays;
}

/**
 * Get the staleness level of an epic
 */
export function getStaleLevel(epic: Epic): 'fresh' | 'aging' | 'stale' | 'critical' {
    const lastActivity = epic.updatedAt || epic.createdAt;
    const days = daysSince(lastActivity);

    if (days <= 7) return 'fresh';
    if (days <= 30) return 'aging';
    if (days <= 60) return 'stale';
    return 'critical';
}

/**
 * Calculate velocity metrics for a set of epics
 */
export function calculateVelocityMetrics(epics: Epic[], sessions: Session[] = []): VelocityMetrics {
    const stalledCount = epics.filter(e => isEpicStalled(e)).length;
    const freshCount = epics.filter(e => getStaleLevel(e) === 'fresh').length;
    const agingCount = epics.filter(e => getStaleLevel(e) === 'aging').length;

    // Calculate lane changes from sessions if available
    let laneChanges = 0;
    if (sessions.length >= 2) {
        const latestSession = sessions[sessions.length - 1];
        const previousSession = sessions[sessions.length - 2];

        if (latestSession?.epics && previousSession?.epics) {
            const previousLanes = new Map(previousSession.epics.map((e: Epic) => [e.id, e.lane]));
            latestSession.epics.forEach((epic: Epic) => {
                const prevLane = previousLanes.get(epic.id);
                if (prevLane && prevLane !== epic.lane) {
                    laneChanges++;
                }
            });
        }
    }

    return {
        stalledCount,
        freshCount,
        agingCount,
        laneChanges,
        healthRatio: epics.length > 0 ? ((epics.length - stalledCount) / epics.length) * 100 : 100
    };
}

/**
 * Get days since last update for display
 */
export function getLastActivityLabel(epic: Epic): string {
    const lastActivity = epic.updatedAt || epic.createdAt;
    const days = daysSince(lastActivity);

    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
}
